import { getConfigForMeasuredTest, GetConfigFunctionArgs } from 'Components/Worksheets/Config';
import { mergeConfig } from 'Components/Worksheets/Core/Helpers/configHelpers';
import {
  getDerivedFromMeasurementsConfigForPortionMassMeasurement,
  getDerivedFromMeasurementsConfigForPortionSieving,
} from 'Components/Worksheets/PsdSieveAnalysisWorksheet/derivedFromMeasurementsConfig';
import { FormatTypes } from 'Lib/Format';
import * as oPath from '../../../../../../Lib/Object/Path/Immutable';
import qestIds from '../../../../QestIds';
import { getSeparationPath } from '../../../Helpers';
import {
  getMeasuredUsingConfigForPortionMassMeasurement,
  getMeasuredUsingConfigForPortionSieving,
} from '../../../measuredUsingConfig';
import getLabelConfig from './labelConfig';

/**
 * Most masses are formatted based on the readability of the corresponding balance.
 * However, some require more complicated logic to determine which balances apply.
 * For these masses, we're currently keeping it simple and displaying them to a fixed 0.1 g.
 */
const massConfig = { uom: 'g', format: { nearest: 0.1 } };

/**
 * Arguments for constructing a config for either ASTM C136
 * or the alias AASHTO T 27
 *
 * These are fixed by QestID so are only used internally here to wrap
 * up an argument-less GetConfigFunction,
 * so we don't need to provide these when getting the config elsewhere
 */
type InternalAstmC136PsdConfigArgs = {
  /**
   * QestId to use for the embedded moisture content test TODO: should be a number
   */
  moistureContentQestId: string;
  /**
   * Additional arguments to customise the returned config object for the moisture content test
   */
  moistureContentAdditionalConfigArgs?: GetConfigFunctionArgs;
  /**
   * QestId to use for the embedded wash test TODO: should be a number
   */
  washQestId: string;
};

const getConfig = (
  prefix: string,
  {
    moistureContentQestId: moistureContentId,
    washQestId: washId,
    moistureContentAdditionalConfigArgs,
  }: InternalAstmC136PsdConfigArgs
) => {
  const maxSeparations = 2;
  let config = {
    [`${prefix}.Preparation.AddSieveSizesBasedOnSpecification`]: {
      options: [
        { value: false, label: 'Based on the selected sieve set' },
        { value: true, label: `Based on the sample's specification` },
      ],
    },
    [`${prefix}.Results.D85`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D60`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D50`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D30`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D15`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D10`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.CoefficientOfCurvature`]: { format: { nearest: 0.01 } },
    [`${prefix}.Results.CoefficientOfUniformity`]: { format: { nearest: 0.01 } },
    [`${prefix}.Results.PercentagePassingWashingSieve`]: { format: { nearest: 0.1 } },
    [`${prefix}.Results.FinenessModulus`]: { format: { nearest: 0.01 } },
    [`${prefix}.ImagesAndRemarks.Remarks`]: { maxLength: 255 },
    [`${prefix}.ImagesAndRemarks.InternalRemarks`]: { maxLength: 255 },
    ...getPortionMassesConfig(
      `${prefix}.WholePortion`,
      'Portion',
      moistureContentId,
      moistureContentAdditionalConfigArgs
    ),
    ...getPortionSievingConfig(`${prefix}.WholePortion`),
    ...getPortionWashingConfig(`${prefix}.WholePortion`, washId),
  };
  // This is fine...
  Object.assign(
    config,
    ...[...Array(maxSeparations).keys()].map((i) => ({
      ...getPortionMassesConfig(
        `${prefix}.${getSeparationPath(i)}.CoarsePortion`,
        'Coarse Portion',
        moistureContentId,
        moistureContentAdditionalConfigArgs
      ),
      ...getPortionSievingConfig(`${prefix}.${getSeparationPath(i)}.CoarsePortion`),
      ...getPortionWashingConfig(`${prefix}.${getSeparationPath(i)}.CoarsePortion`, washId),
      ...getPortionMassesConfig(
        `${prefix}.${getSeparationPath(i)}.FinePortion`,
        'Fine Portion',
        moistureContentId,
        moistureContentAdditionalConfigArgs
      ),
      ...getPortionMassesConfig(
        `${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen`,
        'Subspecimen',
        moistureContentId,
        moistureContentAdditionalConfigArgs
      ),
      ...getPortionSievingConfig(`${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen`),
      ...getPortionWashingConfig(`${prefix}.${getSeparationPath(i)}.FinePortion.Subspecimen`, washId),
    }))
  );

  return mergeConfig(config, getLabelConfig(prefix));
};

const getPortionMassesConfig = (
  portionPath: string,
  materialLabel: string,
  moistureContentId: string,
  moistureContentAdditionalConfigArgs?: GetConfigFunctionArgs
) =>
  mergeConfig(
    {
      [`${portionPath}.Masses.WetMass`]: massConfig,
      [`${portionPath}.Masses.DryMass`]: massConfig,
      ...getPortionMassMeasurementConfig(portionPath),
      [`${portionPath}.Masses.MassUpscaleFactor`]: {
        format: { nearest: 0.01 },
      },
      //Should be based on test method, but this is not available. See QP-3830.
      [`${portionPath}.Masses.MoistureContent`]: {
        format: { nearest: 0.1 },
      },
    },
    getConfigForMeasuredTest(moistureContentId, `${portionPath}.Masses.AssociatedMoistureContent`, {
      ...moistureContentAdditionalConfigArgs,
      materialLabelOverride: materialLabel,
    })
  );

const getPortionWashingConfig = (portionPath: string, washId: string) =>
  mergeConfig(
    {
      [`${portionPath}.Washing.IsWashed`]: { format: { type: FormatTypes.YesNo } },
      [`${portionPath}.Washing.WashedDryMass`]: massConfig,
      [`${portionPath}.Washing.WashingsDryMass`]: massConfig,
      [`${portionPath}.Washing.PercentagePassingWashingSieve`]: { format: { nearest: 0.1 } },
      [`${portionPath}.Washing.AssociatedWashTest`]: { heading: getWashingHeadingFromId(washId) },
    },
    getConfigForMeasuredTest(washId, `${portionPath}.Washing.AssociatedWashTest`)
  );

// This really shouldn't be in config, but it will be too much rework to return a "worksheet type" for a measured test.
const getWashingHeadingFromId = (washId: string) => {
  switch (washId) {
    case qestIds.wash.ASTM_C117.rev2017:
      return 'ASTM C117 Passing No. 200';
    case qestIds.wash.AASHTO_T_11.rev2020:
      return 'AASHTO T 11 Passing No. 200';
  }
  throw new Error(`Unrecognised washing test id: ${washId}`);
};

const getPortionSievingConfig = (portionPath: string) =>
  mergeConfig(
    {
      [`${portionPath}.Sieving.Equipment.SieveSetDimensions.Width`]: (configArgs: any) => {
        const units = oPath.get(configArgs.worksheet, `${portionPath}.Sieving.Equipment.SieveSetUnits`);
        return {
          format: units === 'Metric' ? { nearest: 1 } : { nearest: 0.1 },
          uom: units === 'Metric' ? 'mm' : 'in',
        };
      },
      [`${portionPath}.Sieving.Equipment.SieveSetDimensions.Length`]: (configArgs: any) => {
        const units = oPath.get(configArgs.worksheet, `${portionPath}.Sieving.Equipment.SieveSetUnits`);
        return {
          format: units === 'Metric' ? { nearest: 1 } : { nearest: 0.1 },
          uom: units === 'Metric' ? 'mm' : 'in',
        };
      },
      [`${portionPath}.Sieving.Equipment.SieveSetDimensions.Diameter`]: (configArgs: any) => {
        const units = oPath.get(configArgs.worksheet, `${portionPath}.Sieving.Equipment.SieveSetUnits`);
        return {
          format: units === 'Metric' ? { nearest: 1 } : { nearest: 0.1 },
          uom: units === 'Metric' ? 'mm' : 'in',
        };
      },
      [`${portionPath}.Sieving.RepresentedDryMass`]: massConfig,
      [`${portionPath}.Sieving.Records.MaximumIndividualMassRetained`]: massConfig,
      [`${portionPath}.Sieving.Records.PercentagePassing`]: { format: { nearest: 0.1 } },
      [`${portionPath}.Sieving.Records.PercentageRetained`]: { format: { nearest: 0.1 } },
      [`${portionPath}.Sieving.Records.IndividualPercentageRetained`]: { format: { nearest: 0.1 } },
    },
    getMeasuredUsingConfigForPortionSieving(`${portionPath}.Sieving`),
    getDerivedFromMeasurementsConfigForPortionSieving(`${portionPath}.Sieving`),
    {
      [`${portionPath}.Sieving.Equipment.Balance`]: { defaultReadability: 0.1, defaultUom: 'g' },
    }
  );

const getPortionMassMeasurementConfig = (portionPath: string) =>
  mergeConfig(
    getMeasuredUsingConfigForPortionMassMeasurement(`${portionPath}.Masses.MassMeasurement`),
    getDerivedFromMeasurementsConfigForPortionMassMeasurement(`${portionPath}.Masses.MassMeasurement`),
    {
      [`${portionPath}.Masses.MassMeasurement.Equipment.Balance`]: { defaultReadability: 0.1, defaultUom: 'g' },
      [`${portionPath}.Masses.MassMeasurement.Equipment.DryMassBalance`]: { defaultReadability: 0.1, defaultUom: 'g' },
    }
  );

export default {
  [qestIds.psdSieveAnalysis.ASTM_C136.rev2019]: (prefix: string) =>
    getConfig(prefix, {
      moistureContentQestId: qestIds.moistureContent.ASTM_D_2216.rev2019,
      moistureContentAdditionalConfigArgs: {
        validMethods: ['B'],
      },
      washQestId: qestIds.wash.ASTM_C117.rev2017,
    }),
  [qestIds.psdSieveAnalysis.AASHTO_T_27.rev2020]: (prefix: string) =>
    getConfig(prefix, {
      moistureContentQestId: qestIds.moistureContent.AASHTO_T_265.rev2015,
      washQestId: qestIds.wash.AASHTO_T_11.rev2020,
    }),
};
